import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, forkJoin, EMPTY } from "rxjs";
import { AppConfig } from "../shared/constants/app.config";
import { IPatientContext } from "../model/patient.";

import { UtilityService } from "../shared/service/utility.service";
import { map, expand, reduce } from "rxjs/operators";
import { AngularAppLogger } from "../angular-app-logger";

@Injectable({ providedIn: "root" })
export class EhrPatientService {
  constructor(
    readonly http: HttpClient,
    readonly utilityService: UtilityService,
    readonly appLogger: AngularAppLogger
  ) { }

  getPatient(identifier): Observable<IPatientContext> {
    const url =
      `${this.utilityService.rebuild_DSTU2_STU3_Url(localStorage.getItem("iss"))}/Patient/${identifier}`;
    return this.http.get<IPatientContext>(url, {
      headers: this.buildEhrHeaders(),
    });
  }

  getObservationPriorityLabs(identifier): Observable<any> {
    const requestUrls: any[] = [];
    const splitLoincCodesArray = this.utilityService.chunk(
      AppConfig.LAB_LOINC_CODES.toString().split(","),
      150
    );
    splitLoincCodesArray.forEach((loincs) => {
      const loincCodes = loincs.join(",");
      const issUrl = this.utilityService.rebuild_DSTU2_STU3_Url(localStorage.getItem("iss"));
      const patientIdentifier = "/Observation?patient=" + identifier;
      const greaterThan = "&date=ge" + localStorage.getItem("startDate");
      const lesserThan = "&date=le" + localStorage.getItem("endDate");
      const countQuery = "&_count=1000&code=" + loincCodes;
      const url = issUrl + patientIdentifier + greaterThan + lesserThan + countQuery;
      requestUrls.push(this.getPage(url, this.buildEhrHeaders()));
    });

    return forkJoin(requestUrls).pipe(
      map((res) => {
        const bundles: any[] = [];
        res.forEach((r1: any) => {
          if (r1 instanceof Array) {
            r1.forEach((r) => bundles.push(r));
          }
        });
        return bundles;
      }));
  }

  getObservationLabs(identifier): Observable<any> {
    const issUrl = this.utilityService.rebuild_DSTU2_STU3_Url(localStorage.getItem("iss"));
    const observationQueryStr = "/Observation?category=laboratory";
    const greaterThan = "&date=ge" + localStorage.getItem("startDate");
    const lesserThan = "&date=le" + localStorage.getItem("endDate");
    const patientCount = `&_count=1000&patient=${identifier}`;
    const url = issUrl + observationQueryStr + greaterThan + lesserThan + patientCount;
    return this.getPage(url, this.buildEhrHeaders());
  }

  getMedicationRequest(identifier): Observable<any> {
    const url =
      `${localStorage.getItem("iss")}/MedicationRequest?_count=1000&patient=${identifier}`;
    return this.getPage(url, this.buildEhrHeaders());
  }

  //get Medications() EPIC Specific API
  getMedicationRef(medReferenceRequest): Observable<any> {
    const medRefUrl =
      `${localStorage.getItem("iss")}/${medReferenceRequest}`;
    return this.http.get(medRefUrl, {
      headers: this.buildEhrHeaders(),
    });
  }

  getObservationVitalSigns(identifier): Observable<any>{
    const requestUrls: any[] = [];
    const vitalLoincCodes = AppConfig.VS_LOINC_CODES.toString();
    const issUrl = this.utilityService.rebuild_DSTU2_STU3_Url(localStorage.getItem("iss"));
    const patientIdentifier = "/Observation?patient=" + identifier;
    const greaterThan = "&date=ge" + localStorage.getItem("startDate");
    const lesserThan = "&date=le" + localStorage.getItem("endDate");
    const countQuery = "&_count=1000&code=" + vitalLoincCodes;
    const url = issUrl + patientIdentifier + greaterThan + lesserThan + countQuery;
    return this.getPage(url, this.buildEhrHeaders());
  }

  buildEhrHeaders() {
    const ehrHeaders: HttpHeaders = new HttpHeaders()
      .set("Content-Type", "application/json")
      .set("Accept", "application/json")
      .set(
        "Authorization",
        `Bearer ${localStorage.getItem("accessToken")}`
      );

    return ehrHeaders;
  }

  getPage(url, theHeaders) {
    return this.http.get(url, { headers: theHeaders }).pipe(
      expand((response: any) => {
        const next =
          response.link && response.link.find((l) => l.relation === "next");
        if (next) {
          return this.http.get(next.url, { headers: theHeaders });
        } else {
          return EMPTY;
        }
      }),
      map((response: any) => {
        if (response.entry) {
          return response.entry.flatMap((array) => array);
        }
        return [];
      }),
      reduce((acc: any[], x: any) => acc.concat(x), [])
    );
  }
}
